class FrontpageCarousel {
  constructor(sectionId) {
    this.sectionId = sectionId;
    this.carouselId = `FrontpageCarousel-${sectionId}`;
    this.dotsId = `FrontpageCarouselDots-${sectionId}`;
    
    this.wrapper = null;
    this.dotsContainer = null;
    this.dots = [];
    this.cards = [];
    this.originalCards = [];
    
    this.currentSlide = 0;
    this.isTransitioning = false;
    this.autoSlideInterval = null;
    this.totalSlides = 0;
    this.isInfiniteLoop = true;
    
    this.init();
  }
  
  init() {
    this.wrapper = document.getElementById(this.carouselId);
    this.carouselRoot = this.wrapper?.closest('.frontpage-carousel');
    this.dotsContainer =
      this.carouselRoot?.querySelector('[data-buso-carousel-dots]') ||
      document.getElementById(this.dotsId);

    if (!this.wrapper || !this.dotsContainer) {
      console.warn('FrontpageCarousel: Required elements not found');
      return;
    }

    this.dots = this.dotsContainer.querySelectorAll('.buso-carousel__dot');
    this.cards = this.wrapper.querySelectorAll('.frontpage-carousel__card');
    
    // Filter out clone cards to get original cards only
    this.originalCards = Array.from(this.cards).filter(card => !card.classList.contains('frontpage-carousel__card--clone'));
    this.totalSlides = this.originalCards.length;
    
    if (this.totalSlides === 0) {
      console.warn('FrontpageCarousel: No carousel cards found');
      return;
    }
    
    if (this.totalSlides <= 1) {
      this.setupSingleSlide();
      return;
    }
    
    this.setupCarousel();
  }
  
  setupSingleSlide() {
    this.wrapper.style.transform = 'translateX(0)';
    // Make the single card visually active; avoid scale on mobile
    const card = this.cards[0];
    if (window.innerWidth >= 750) {
      card.classList.add('scale-100');
      card.classList.remove('scale-95');
    } else {
      card.classList.remove('scale-100', 'scale-95');
    }
    card.classList.add('opacity-100', 'z-[2]');
    card.classList.remove('opacity-70');
    const img = card.querySelector('.frontpage-carousel__image');
    if (img) {
      img.classList.add('filter', 'transition-all', 'duration-700', 'ease-in-out', 'brightness-110');
      img.classList.remove('brightness-75');
    }
    this.dotsContainer.style.display = 'none';
  }
  
  setupCarousel() {
    // Set initial state - start at the first original card (index 1 due to clone at start)
    this.currentSlide = 0;
    this.goToSlide(0, true);
    
    // Ensure clone cards are properly positioned and visible
    this.ensureCloneVisibility();
    
    this.addEventListeners();
    
    // Start auto-rotation after delay
    setTimeout(() => {
      this.startAutoSlide();
    }, 2000); // Reduced initial delay from 3000ms to 2000ms
  }

  getTranslateXForCard(actualCardIndex) {
    const referenceCard = this.cards[0];
    if (!referenceCard || !this.wrapper || !this.wrapper.parentElement) {
      return 0;
    }

    // Use untransformed layout width.
    // `getBoundingClientRect()` includes `transform: scale(...)`, which changes after
    // `updateActiveCard()` and makes our centering math drift on subsequent slides.
    const cardWidth = referenceCard.offsetWidth || referenceCard.getBoundingClientRect().width;
    const wrapperStyles = getComputedStyle(this.wrapper);
    const gap = parseFloat(wrapperStyles.columnGap || wrapperStyles.gap || '0') || 0;
    const containerWidth = this.wrapper.parentElement.getBoundingClientRect().width;

    const span = cardWidth + gap;
    const centeredLeft = (containerWidth - cardWidth) / 2;
    return -(actualCardIndex * span) + centeredLeft;
  }
  
  ensureCloneVisibility() {
    // Ensure clone cards are properly positioned and visible
    this.cards.forEach((card, index) => {
      if (card.classList.contains('frontpage-carousel__card--clone')) {
        // Make sure clone cards are visible but not active initially
        card.style.display = 'flex';
        card.style.visibility = 'visible';
      }
    });
  }

  updateDots() {
    this.dots.forEach((dot, index) => {
      const isActive = index === this.currentSlide;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }
  
  updateActiveCard() {
    const applyActiveStyles = (el, makeActive) => {
      const isDesktop = window.innerWidth >= 750;
      if (isDesktop) {
        el.classList.toggle('scale-100', makeActive);
        el.classList.toggle('scale-95', !makeActive);
        el.classList.toggle('opacity-100', makeActive);
        el.classList.toggle('opacity-70', !makeActive);
        el.classList.toggle('z-[2]', makeActive);
        el.classList.toggle('z-[1]', !makeActive);
      } else {
        el.classList.remove('scale-100', 'scale-95');
        // Keep mobile card text readable while sliding between positions.
        el.classList.remove('opacity-70');
        el.classList.add('opacity-100');
        // Keep active card above neighbors so images never cover its text.
        el.classList.toggle('z-[2]', makeActive);
        el.classList.toggle('z-[1]', !makeActive);
      }
      // Brightness animation on image
      const img = el.querySelector('.frontpage-carousel__image');
      if (img) {
        img.classList.add('filter', 'transition-all', 'duration-700', 'ease-in-out');
        img.classList.toggle('brightness-110', makeActive);
        img.classList.toggle('brightness-75', isDesktop && !makeActive);
        img.classList.toggle('brightness-95', !isDesktop && !makeActive);
      }
    };

    // Update original cards
    this.originalCards.forEach((card, index) => {
      applyActiveStyles(card, index === this.currentSlide);
    });

    // Update clones to mirror active state at edges
    this.cards.forEach((card, index) => {
      if (!card.classList.contains('frontpage-carousel__card--clone')) return;
      const isFirstClone = index === 0;
      const isLastClone = index === this.cards.length - 1;
      const shouldBeActive = (isFirstClone && this.currentSlide === this.totalSlides - 1)
        || (isLastClone && this.currentSlide === 0);
      applyActiveStyles(card, shouldBeActive);
    });
  }
  
  goToSlide(slideIndex, instant = false) {
    if (this.isTransitioning || slideIndex < 0 || slideIndex >= this.totalSlides) {
      return;
    }
    
    this.isTransitioning = true;
    this.currentSlide = slideIndex;
    
    // Calculate the actual card index (accounting for the clone at the beginning)
    const actualCardIndex = slideIndex + 1; // +1 because first card is a clone
    
    const translateX = this.getTranslateXForCard(actualCardIndex);
    this.wrapper.style.transform = `translateX(${translateX}px)`;
    
    this.wrapper.style.transition = instant ? 'none' : 'transform 0.45s ease-in-out';
    
    this.updateActiveCard();
    this.updateDots();
    
    if (!instant) {
      setTimeout(() => {
        this.isTransitioning = false;
      }, 450);
    } else {
      this.isTransitioning = false;
    }
  }
  
  nextSlide() {
    if (this.currentSlide === this.totalSlides - 1) {
      // If we're at the last slide, we need to handle the infinite loop
      this.handleInfiniteLoop('next');
    } else {
      const nextIndex = this.currentSlide + 1;
      this.goToSlide(nextIndex);
    }
  }
  
  prevSlide() {
    if (this.currentSlide === 0) {
      // If we're at the first slide, we need to handle the infinite loop
      this.handleInfiniteLoop('prev');
    } else {
      const prevIndex = this.currentSlide - 1;
      this.goToSlide(prevIndex);
    }
  }
  
  handleInfiniteLoop(direction) {
    if (this.isTransitioning) return;
    
    this.isTransitioning = true;
    
    if (direction === 'next') {
      // Move to the clone of the first card (last position)
      const actualCardIndex = this.totalSlides + 1; // Last clone position
      const translateX = this.getTranslateXForCard(actualCardIndex);
      
      // Ensure the clone is visible before transitioning
      const lastClone = this.cards[this.cards.length - 1];
      if (lastClone && lastClone.classList.contains('frontpage-carousel__card--clone')) {
        lastClone.style.display = 'flex';
        lastClone.style.visibility = 'visible';
      }
      
      this.wrapper.style.transform = `translateX(${translateX}px)`;
      this.wrapper.style.transition = 'transform 0.45s ease-in-out';
      
      // After transition, instantly jump to the real first card
      setTimeout(() => {
        this.wrapper.style.transition = 'none';
        this.currentSlide = 0;
        const firstCardIndex = 1; // First real card position
        const firstTranslateX = this.getTranslateXForCard(firstCardIndex);
        this.wrapper.style.transform = `translateX(${firstTranslateX}px)`;
        
        this.updateActiveCard();
        this.updateDots();
        this.isTransitioning = false;
      }, 450);
      
    } else if (direction === 'prev') {
      // Move to the clone of the last card (first position)
      const actualCardIndex = 0; // First clone position
      const translateX = this.getTranslateXForCard(actualCardIndex);
      
      // Ensure the clone is visible before transitioning
      const firstClone = this.cards[0];
      if (firstClone && firstClone.classList.contains('frontpage-carousel__card--clone')) {
        firstClone.style.display = 'flex';
        firstClone.style.visibility = 'visible';
      }
      
      this.wrapper.style.transform = `translateX(${translateX}px)`;
      this.wrapper.style.transition = 'transform 0.45s ease-in-out';
      
      // After transition, instantly jump to the real last card
      setTimeout(() => {
        this.wrapper.style.transition = 'none';
        this.currentSlide = this.totalSlides - 1;
        const lastCardIndex = this.totalSlides; // Last real card position
        const lastTranslateX = this.getTranslateXForCard(lastCardIndex);
        this.wrapper.style.transform = `translateX(${lastTranslateX}px)`;
        
        this.updateActiveCard();
        this.updateDots();
        this.isTransitioning = false;
      }, 450);
    }
  }
  
  startAutoSlide() {
    if (this.totalSlides <= 1) return;
    
    this.stopAutoSlide();
    this.autoSlideInterval = setInterval(() => {
      this.nextSlide();
    }, 5000); // Increased from 4000ms to 5000ms for better timing
  }
  
  stopAutoSlide() {
    if (this.autoSlideInterval) {
      clearInterval(this.autoSlideInterval);
      this.autoSlideInterval = null;
    }
  }
  
  addEventListeners() {
    const carouselContainer = this.carouselRoot || this.wrapper.closest('.frontpage-carousel');
    const prevButton = carouselContainer.querySelector('[data-buso-carousel-prev]');
    const nextButton = carouselContainer.querySelector('[data-buso-carousel-next]');
    
    if (prevButton) {
      prevButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.prevSlide();
        this.stopAutoSlide();
        this.startAutoSlide();
      });
    }
    
    if (nextButton) {
      nextButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.nextSlide();
        this.stopAutoSlide();
        this.startAutoSlide();
      });
    }
    
    // Dot navigation
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.goToSlide(index);
        this.stopAutoSlide();
        this.startAutoSlide();
      });
    });
    
    
    // Removed hover pause on button to keep auto-rotation continuous
    
    // Add click functionality to cards
    this.addCardClickListeners();
    
    // Touch/swipe support for mobile devices
    let startX = 0;
    let startY = 0;
    let endX = 0;
    let endY = 0;
    let isDragging = false;
    
    this.wrapper.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      isDragging = true;
      this.stopAutoSlide();
    }, { passive: true });
    
    this.wrapper.addEventListener('touchmove', (e) => {
      if (!isDragging) return;
      
      // Prevent default scrolling while swiping horizontally
      const currentX = e.touches[0].clientX;
      const currentY = e.touches[0].clientY;
      const diffX = Math.abs(currentX - startX);
      const diffY = Math.abs(currentY - startY);
      
      if (diffX > diffY) {
        e.preventDefault();
      }
    }, { passive: false });
    
    this.wrapper.addEventListener('touchend', (e) => {
      if (!isDragging) return;
      
      endX = e.changedTouches[0].clientX;
      endY = e.changedTouches[0].clientY;
      
      const diffX = startX - endX;
      const diffY = startY - endY;
      
      // Only trigger swipe if horizontal movement is greater than vertical and sufficient distance
      if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 30) {
        if (diffX > 0) {
          // Swipe left - go to next slide
          this.nextSlide();
        } else {
          // Swipe right - go to previous slide
          this.prevSlide();
        }
      }
      
      isDragging = false;
      
      // Restart auto-slide after a short delay
      setTimeout(() => {
        this.startAutoSlide();
      }, 1000);
    }, { passive: true });
    
    // Handle window resize
    window.addEventListener('resize', () => {
      this.goToSlide(this.currentSlide, true);
    });
  }
  
  addCardClickListeners() {
    // Add click listeners to all cards (including clones)
    this.cards.forEach((card, index) => {
      // Determine the original card index for clone cards
      let originalIndex = index;
      let isClone = card.classList.contains('frontpage-carousel__card--clone');
      
      if (isClone) {
        // First clone (index 0) corresponds to the last original card
        if (index === 0) {
          originalIndex = this.totalSlides - 1;
        }
        // Last clone corresponds to the first original card
        else if (index === this.cards.length - 1) {
          originalIndex = 0;
        }
      } else {
        // For original cards, adjust index (subtract 1 because first card is a clone)
        originalIndex = index - 1;
      }
      
      // Ensure pointer events are enabled for all cards
      card.style.pointerEvents = 'auto';
      
      card.addEventListener('click', (e) => {
        // Prevent default if clicking on button (button should handle its own click)
        if (e.target.closest('.frontpage-carousel__button')) {
          return;
        }
        
        // On mobile we don't use scale classes, so compare with current slide index.
        const isActiveCard = window.innerWidth >= 750
          ? card.classList.contains('scale-100')
          : originalIndex === this.currentSlide;
        if (isActiveCard) {
          // Center card clicked - open product page
          this.handleCenterCardClick(card);
        } else {
          // Side card clicked - move to center
          if (isClone) {
            // For clone cards, simulate arrow button behavior
            if (index === 0) {
              // First clone (last card) - simulate prev button to go to last real card
              this.prevSlide();
            } else if (index === this.cards.length - 1) {
              // Last clone (first card) - simulate next button to go to first real card
              this.nextSlide();
            }
          } else {
            // For original cards, normal slide transition
            this.goToSlide(originalIndex);
            this.stopAutoSlide();
            this.startAutoSlide();
          }
        }
      });
      
      // Add cursor pointer style for clickable cards
      card.style.cursor = 'pointer';
    });
  }
  
  handleCenterCardClick(card) {
    // Find the button URL in the card
    const button = card.querySelector('.frontpage-carousel__button');
    if (button && button.href && button.href !== '#') {
      // Navigate to the button URL
      window.location.href = button.href;
    }
  }
  
  destroy() {
    this.stopAutoSlide();
  }
}

// Initialize carousels when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  const carouselSections = document.querySelectorAll('[id^="FrontpageCarousel-"]');
  
  carouselSections.forEach(carouselElement => {
    const sectionId = carouselElement.id.replace('FrontpageCarousel-', '');
    new FrontpageCarousel(sectionId);
  });
});